import { differenceInCalendarDays } from 'date-fns';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Icon } from '../../../components/common/icon/Icon';
import { ISubsidy } from '../../../interfaces/Subsidy.interfaces';
import { formatDateWithDot } from '../../../utils/formatter/formatDate.util';
import { postMessageToRN } from '../../../utils/RNCommunicator/postMessageToRN.util';
import { ampli } from '../../../ampli';

import { vibrateWithDuration } from '../../../utils/vibration.util';

interface SubsidyVerticalListViewProps {
  subsidy: ISubsidy;
}
export const SubsidyVerticalListView = ({ subsidy }: SubsidyVerticalListViewProps) => {
  const getDeadlineInfo = () => {
    // 1. Check deadlineType first
    if (subsidy.deadlineType === 'MULTIPLE') {
      return { text: '공고참조', color: '#232429' };
    }
    if (subsidy.deadlineType === 'ALWAYS') {
      return { text: '상시모집', color: '#232429' };
    }

    // 2. Fallback to existing logic (FIXED or null)
    if (!subsidy.deadline) {
      return { text: '공고참조', color: '#232429' };
    }

    const today = new Date();
    const deadlineDate = new Date(subsidy.deadline);
    const diffDays = differenceInCalendarDays(deadlineDate, today);

    const color = diffDays < 7 ? '#FF3049' : '#232429';
    const deadlineText = diffDays === 0 ? '오늘' : `${diffDays}일 남음`;

    return { text: `마감: ${deadlineText}`, color };
  };

  const deadlineInfo = getDeadlineInfo();

  const handleItemClick = () => {
    // 짧고 약한 진동
    vibrateWithDuration(20);

    if (subsidy.link) {
      if (window.ReactNativeWebView) {
        postMessageToRN({ messageKey: 'link', messageParam: subsidy.link });
      } else {
        window.open(subsidy.link, '_blank');
      }

      ampli.pvmClickSubsidyCard({
        subsidy_id: subsidy.id,
        subsidy_title: subsidy.title,
        state: subsidy.state ?? '',
        city: subsidy.city ?? '',
        subsidy_tag_ids: subsidy.tags?.map((tag) => tag.id) ?? [],
        subsidy_tag_names: subsidy.tags?.map((tag) => tag.name) ?? [],
      });
    }
  };

  const isNew = subsidy.postedAt && differenceInCalendarDays(new Date(), new Date(subsidy.postedAt)) <= 3;

  return (
    <Container onClick={handleItemClick}>
      <InfoSection>
        <HeaderContainer>
          <HeaderLeftSection>
            {isNew && <NewBadge>{'NEW'}</NewBadge>}
            <Region>
              {subsidy.isNationwide
                ? '전국'
                : `${
                    ['전북특별자치도', '전북'].includes(subsidy.state ?? '')
                      ? '전북'
                      : ['전라남도', '전남'].includes(subsidy.state ?? '')
                        ? '전남'
                        : subsidy.state ?? ''
                  } ${subsidy.city ?? ''}`.trim()}
              {subsidy.postedAt && <PostedAt>{` | ${formatDateWithDot(new Date(subsidy.postedAt))}`}</PostedAt>}
            </Region>
          </HeaderLeftSection>
          {subsidy.category && <Category>{subsidy.category}</Category>}
        </HeaderContainer>

        <TextSection>
          <Title>{subsidy.title}</Title>
          {subsidy.benefitSummary && <BenefitSummary>{`${subsidy.benefitSummary}`}</BenefitSummary>}
          <BottomSection>
            <DeadlineSection>
              <Icon name={'Clock'} width={14} height={14} fill={deadlineInfo.color} />
              <DeadlineText style={{ color: deadlineInfo.color }}>{deadlineInfo.text}</DeadlineText>
            </DeadlineSection>
          </BottomSection>
        </TextSection>
      </InfoSection>
      <ArrowSection>
        <Icon name={'ArrowRight'} width={18} height={18} fill={'#FFFFFF'} />
      </ArrowSection>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: stretch;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
  overflow: hidden;

  cursor: pointer;
`;

const InfoSection = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;

  gap: 12px;

  padding: 20px;
`;

const HeaderContainer = styled.div`
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderLeftSection = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;

  gap: 4px;
`;

const NewBadge = styled.span`
  color: #ff3049;
  font-family: Pretendard;
  font-size: 11px;
  font-weight: 700;
  line-height: normal;
`;

const Department = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%;
`;

const Region = styled.span`
  color: var(--light-scheme-color-primary-blue, #445eff);
  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;

  display: flex;
  align-items: center;
`;

const PostedAt = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 400;
  margin-left: 2px;
`;

const SplitBar = styled.span`
  color: var(--Light-Scheme-Monochrome-grey50, #babdca);
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%;
`;

const Category = styled.span`
  color: var(--light-scheme-color-primary-blue, #445eff);
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 85%;
  background: #f0f4ff;
  padding: 4px 8px;
  border-radius: 4px;
`;

const Title = styled.h2`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  align-self: stretch;

  overflow: hidden;
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-overflow: ellipsis;
  word-break: break-all;
  margin: 0;

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%;
`;

const BenefitSummary = styled.span`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  align-self: stretch;

  overflow: hidden;
  color: var(--Light-Scheme-Monochrome-grey80, #6c6f7d);
  text-overflow: ellipsis;

  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`;

const DeadlineSection = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const BottomSection = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
`;

const ViewAnnouncementButton = styled.button`
  display: flex;
  height: 28px;
  padding: 0 10px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  border: 1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3);
  background: #fff;

  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  /* System/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 10.2px */
`;

const DeadlineText = styled.span`
  color: var(--light-scheme-color-primary-red, #ff3049);
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%;
`;

const TextSection = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;

  gap: 4px;
`;

const ArrowSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 48px;
  background-color: var(--Light-Scheme-Monochrome-grey60, #9da2ab);
`;
